import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";

const EASE = [0.22, 1, 0.36, 1];

const CHALLENGES = [
  {
    index: "01",
    label: "Trusted information",
    copy: "Consumers still struggle to find trusted financial information.",
  },
  {
    index: "02",
    label: "Acquisition cost",
    copy: "Institutions face rising customer acquisition costs.",
  },
  {
    index: "03",
    label: "Rising fraud",
    copy: "Fraud continues to increase across digital channels.",
  },
  {
    index: "04",
    label: "Recovery efficiency",
    copy: "Debt recovery remains expensive and inefficient.",
  },
];

const CAPABILITIES = [
  "Intelligent financial education",
  "AI-powered recommendations",
  "Digital verification",
  "Automated collections",
];

const NETWORK = [
  { index: "01", name: "Consumers" },
  { index: "02", name: "Banks" },
  { index: "03", name: "FinTech Companies" },
  { index: "04", name: "Insurance Companies" },
  { index: "05", name: "Savings & Loans Institutions" },
  { index: "06", name: "Microfinance Institutions" },
  { index: "07", name: "Investment Firms" },
  { index: "08", name: "Pension Providers" },
  { index: "09", name: "Credit Providers" },
];

function About() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="about-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(229,184,11,0.1),transparent_55%),radial-gradient(60%_40%_at_100%_20%,rgba(12,31,27,0.06),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-4xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Welcome to SimFinance
          </p>
          <h2
            id="about-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Transforming the future of
            <span className="mt-1 block">financial services</span>
          </h2>
        </motion.header>

        <motion.ol
          className="mt-14 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: reduce ? 0 : 0.08 },
            },
          }}
        >
          {CHALLENGES.map((item) => (
            <motion.li
              key={item.index}
              className="relative bg-white px-5 py-6 sm:px-6 sm:py-8"
              variants={fade}
            >
              <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                {item.index}
              </span>
              <p className="mt-4 text-lg font-semibold tracking-tight text-navy">
                {item.label}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-navy/55">
                {item.copy}
              </p>
            </motion.li>
          ))}
        </motion.ol>

        <div className="mt-16 grid items-stretch gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12">
          <motion.div
            className="flex flex-col justify-between"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/40">
                The system
              </p>
              <p className="mt-5 text-lg leading-[1.75] text-navy/75">
                SimFinance solves these challenges by bringing together intelligent
                financial education, AI-powered recommendations, digital verification,
                and automated collections into one powerful ecosystem.
              </p>
              <p className="mt-5 text-lg leading-[1.75] text-navy/60">
                From helping consumers discover the right financial products to enabling
                institutions to verify borrowers and recover outstanding debts, SimFinance
                transforms every stage of the financial services lifecycle.
              </p>
            </div>

            <ul className="mt-10 grid gap-3 sm:grid-cols-2">
              {CAPABILITIES.map((item, index) => (
                <li
                  key={item}
                  className="flex items-start gap-3 border-t border-navy/10 pt-3"
                >
                  <span className="mt-0.5 font-mono text-[10px] font-semibold tracking-[0.18em] text-royal">
                    0{index + 1}
                  </span>
                  <span className="text-sm font-medium leading-snug text-navy">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="relative lg:min-h-full"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <LandscapePanel reduce={reduce} />
          </motion.div>
        </div>

        <motion.div
          className="mt-24"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                The network
              </p>
              <h3 className="mt-3 text-3xl font-semibold tracking-tight text-royal sm:text-4xl">
                Our ecosystem empowers
              </h3>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-navy/50">
              Built as one intelligent digital platform for the institutions and people
              that move African finance.
            </p>
          </div>

          <div className="mt-10 overflow-hidden border border-navy/10 bg-navy/10">
            <ul className="grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-3">
              {NETWORK.map((item) => (
                <li key={item.name} className="bg-white">
                  <a
                    href="#contact"
                    className="group flex h-full min-h-[8.5rem] cursor-pointer flex-col justify-between p-5 transition hover:bg-navy hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:min-h-[9.5rem] sm:p-6"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                        {item.index}
                      </span>
                      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    </span>
                    <span className="text-base font-semibold leading-snug tracking-tight sm:text-lg">
                      {item.name}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.p
          className="mt-16 max-w-3xl font-semibold leading-snug tracking-tight text-royal [font-size:clamp(1.5rem,3.2vw,2.35rem)]"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          Through one intelligent digital platform{" "}
          <span className="text-royal">built for Africa</span>.
        </motion.p>
      </div>
    </section>
  );
}

function LandscapePanel({ reduce }) {
  const stages = [
    { index: "01", label: "Discover" },
    { index: "02", label: "Verify" },
    { index: "03", label: "Recover" },
  ];

  return (
    <div className="relative h-full min-h-[22rem] lg:min-h-[32rem]">
      <div
        className="pointer-events-none absolute -inset-x-3 -inset-y-3 border border-gold/30"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute -right-2 -top-2 h-16 w-16 border-r border-t border-navy/20" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-2 -left-2 h-16 w-16 border-b border-l border-gold/40" aria-hidden="true" />

      <div className="relative h-full min-h-[22rem] overflow-hidden bg-navy lg:min-h-[32rem]">
        <MediaFrame
          src={Images.aboutLandscape}
          alt="SimFinance connecting Africa’s financial institutions and consumers through one digital ecosystem"
          label="01  ·  Landscape"
          position="object-center"
          className="absolute inset-0 h-full w-full"
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/25" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/80 to-transparent" />

        <div className="absolute left-5 top-5 flex items-center gap-3 sm:left-6 sm:top-6">
          <span className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
            01 / 03
          </span>
          <span className="h-px w-8 bg-white/30" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70">
            Africa
          </span>
        </div>

       

        <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6">
          <div className="mb-4 flex flex-wrap gap-2">
            {stages.map((stage, index) => (
              <motion.span
                key={stage.label}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-navy/55 px-3 py-1.5 backdrop-blur-sm"
                initial={reduce ? false : { opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : { duration: 0.4, delay: 0.12 * index, ease: EASE }
                }
              >
                <span className="font-mono text-[10px] text-gold">{stage.index}</span>
                <span className="text-[11px] font-semibold tracking-wide text-white">
                  {stage.label}
                </span>
              </motion.span>
            ))}
          </div>

          <div className="max-w-md border-t border-white/15 pt-4">
            <p className="text-sm font-medium leading-relaxed text-white">
              One ecosystem. Education, intelligence, trust and recovery — in a single
              platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaFrame({ src, alt, label, className, position = "object-center" }) {
  const [failed, setFailed] = useState(false);
  const showFallback = failed || !src;

  if (showFallback) {
    return (
      <div
        className={`relative overflow-hidden bg-navy ${className}`}
        role="img"
        aria-label={alt}
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -right-10 top-8 h-40 w-40 rounded-full bg-royal/35 blur-3xl" />
        <div className="absolute -bottom-12 left-6 h-32 w-32 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative flex h-full min-h-[inherit] flex-col justify-between p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold/90">
            {label}
          </p>
          <p className="max-w-[14rem] text-sm leading-relaxed text-white/55">
            Placeholder — generate and drop the image into this slot.
          </p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover ${position} ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

export default About;
